import { Component, EventEmitter, HostBinding, Input, OnInit, Output, ViewChild } from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { NumeralPipe } from '../numeral.pipe';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import {TranslateService} from '@ngx-translate/core';

const DEFAULT_PAGE_SIZE = 50;

@Component({
   selector: 'app-paginator',
   templateUrl: './paginator.component.html',
   styleUrls: ['./paginator.component.scss'],
   providers: [NumeralPipe]
})
export class PaginatorComponent implements OnInit {
   @HostBinding('class') get className() { return 'app-paginator'; }
   private _count = 0;
   private _page = 0;
   private _size: number;
   private _pageSizeOptions: number[] = [50, 100, 150, 200];
   _displayedPageSizeOptions: number[];
   private _showFirstLastButtons = true;
   @Output() readonly pageChange: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
   @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
   menuOpened = false;
   private OF_LABEL = 'of';

   constructor(private numeral: NumeralPipe, private translate: TranslateService) { }

   ngOnInit() {
      this._updateDisplayedPageSizeOptions();
      this._getAndInitTranslations();
   }

   @Input()
   get count(): number { return this._count; }
   set count(value: number) {
      this._count = value;
   }

   @Input()
   get page(): number { return this._page; }
   set page(value: number) {
      this._page = value < 0 ? 0 : value;
   }

   @Input()
   get size(): number { return this._size; }
   set size(value: number) {
      this._size = value < 0 ? 0 : value;
      this._updateDisplayedPageSizeOptions();
   }

   @Input()
   get pageSizeOptions(): number[] { return this._pageSizeOptions; }
   set pageSizeOptions(value: number[]) {
      this._pageSizeOptions = value || [];
      this._updateDisplayedPageSizeOptions();
   }

   @Input()
   get showFirstLastButtons(): boolean { return this._showFirstLastButtons; }
   set showFirstLastButtons(value: boolean) {
      this._showFirstLastButtons = value;
   }

   nextPage(): void {
      if (!this.hasNextPage()) { return; }

      const previousPageIndex = this.page;
      this.page++;
      this._emitPageEvent(previousPageIndex);
   }

   previousPage(): void {
      if (!this.hasPreviousPage()) { return; }

      const previousPageIndex = this.page;
      this.page--;
      this._emitPageEvent(previousPageIndex);
   }

   firstPage(): void {
      if (!this.hasPreviousPage()) { return; }

      const previousPageIndex = this.page;
      this.page = 0;
      this._emitPageEvent(previousPageIndex);
   }

   lastPage(): void {
      if (!this.hasNextPage()) { return; }

      const previousPageIndex = this.page;
      this.page = this.getNumberOfPages() - 1;
      this._emitPageEvent(previousPageIndex);
   }

   hasPreviousPage(): boolean {
      return this.page >= 1 && this.size !== 0;
   }

   hasNextPage(): boolean {
      const maxPageIndex = this.getNumberOfPages() - 1;
      return this.page < maxPageIndex && this.size !== 0;
   }

   getNumberOfPages(): number {
      if (!this.size) {
         return 0;
      }
      return Math.ceil(this.count / this.size);
   }

   _changePageSize(pageSize: number) {
      const startIndex = this.page * this.size;
      const previousPageIndex = this.page;

      this.page = Math.floor(startIndex / pageSize) || 0;
      this.size = pageSize;
      this._emitPageEvent(previousPageIndex);
   }

   _nextButtonsDisabled() {
      return !this.hasNextPage();
   }

   _previousButtonsDisabled() {
      return !this.hasPreviousPage();
   }

   private _updateDisplayedPageSizeOptions() {
      if (!this.size) {
         this._size = this.pageSizeOptions.length > 0 ? this.pageSizeOptions[0] : DEFAULT_PAGE_SIZE;
      }

      this._displayedPageSizeOptions = this.pageSizeOptions.slice();

      if (this._displayedPageSizeOptions.indexOf(this.size) === -1) {
         this._displayedPageSizeOptions.push(this.size);
      }

      this._displayedPageSizeOptions.sort((a, b) => a - b);
   }

   private _getAndInitTranslations() {
      this.translate.get('flow.of').subscribe(s => this.OF_LABEL = s);
   }

   private _emitPageEvent(previousPageIndex: number) {
      this.pageChange.emit({
         previousPageIndex,
         pageIndex: this.page,
         pageSize: this.size,
         length: this.count
      });
   }

   getRangeLabel(): string {
      if (this.count === 0 || this.size === 0) { return `0 ${this.OF_LABEL} ${this.count}`; }

      const count = Math.max(this.count, 0);
      const offset = this.page * this.size;
      // If the start index exceeds the list count, do not try and fix the end index to the end.
      const endIndex = offset < count ? Math.min(offset + this.size, count) : offset + this.size;

      const start = this.numeral.transform(offset + 1);
      const last = this.numeral.transform(endIndex);
      const total = this.numeral.transform(this.count);
      return `${start}–${last} ${this.OF_LABEL} ${total}`;
   }

   clickItemPerPage(): void {
      if (this._displayedPageSizeOptions.length > 1 && this.count >= this._displayedPageSizeOptions[0]) {
         this.menuTrigger.openMenu();
      }
   }

   delayClose(): void {
      setTimeout(() => { this.menuOpened = false; }, 100);
   }
}
